import {useContext, useMemo} from "react";

import useTeacherAccessPermissionsQuery, {
    type TeacherAccessPermissionsResult,
} from "../queries/use-teacher-access-permissions-query.ts";
import {TeacherAccessPermissionsContext} from "../teacher-access-permissions-context.ts";

/**
 * Hook to access the teacher eligibility context.
 * This is used to gate access to certain teacher tools.
 */
const useTeacherAccessPermissions = (): TeacherAccessPermissionsResult => {
    const teacherAccessPermissionsContext = useContext(
        TeacherAccessPermissionsContext,
    );

    // Skip the query if we have a valid context value.
    const validContext = teacherAccessPermissionsContext.status !== "loading";

    const queryResult = useTeacherAccessPermissionsQuery({
        skip: validContext,
    });

    const resultToUse = validContext
        ? teacherAccessPermissionsContext
        : queryResult;
    const memoizedResult = useMemo<TeacherAccessPermissionsResult>(() => {
        if (resultToUse.status !== "success") {
            return {
                status: resultToUse.status,
                data: null,
            };
        }
        return {
            status: resultToUse.status,
            data: {
                isEligibleForKhanmigoTeacherTools:
                    resultToUse.data.isEligibleForKhanmigoTeacherTools,
                canAccessKhanmigoTeacherTools:
                    resultToUse.data.canAccessKhanmigoTeacherTools,
                canAssignAssignments: resultToUse.data.canAssignAssignments,
                canPreviewLoggedOutTools:
                    resultToUse.data.canPreviewLoggedOutTools,
            },
        };
    }, [
        resultToUse.status,
        resultToUse.data?.isEligibleForKhanmigoTeacherTools,
        resultToUse.data?.canAccessKhanmigoTeacherTools,
        resultToUse.data?.canAssignAssignments,
        resultToUse.data?.canPreviewLoggedOutTools,
    ]);
    return memoizedResult;
};

export default useTeacherAccessPermissions;
